import api from '@/lib/api';
import { ROUTES } from '@/constants/routes';
import {
  FirmStatsResponse,
  FirmStatistics,
  UserStatsResponse,
  UserStatistics,
  ClientStatsResponse,
  ClientStatistics,
  TeamOverviewResponse,
  TopClientsResponse,
  FirmOverviewResponse,
  TrendAnalysisResponse,
  StatsQueryParams,
  ComparisonParams,
  TopClientsParams,
  StatsErrorResponse,
} from '@/types/statistics';

class StatsApiService {
  private baseUrl = ROUTES.API.STATISTICS;

  // Helper method to build query string
  private buildQueryString(params: Record<string, any>): string {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, value.toString());
      }
    });
    
    const queryString = searchParams.toString();
    return queryString ? `?${queryString}` : '';
  }

  // Firm Statistics
  async getFirmStats(params: StatsQueryParams = {}): Promise<FirmStatsResponse> {
    try {
      const queryString = this.buildQueryString(params);
      const response = await api.get(`${this.baseUrl}firms/${queryString}`);
      console.log(response);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getFirmStatsById(firmId: number, params: StatsQueryParams = {}): Promise<FirmStatistics> {
    try {
      const queryString = this.buildQueryString(params);
      const response = await api.get(`${this.baseUrl}firms/${firmId}/${queryString}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // New method for comprehensive firm statistics
  async getComprehensiveFirmStats(firmId: number, params: StatsQueryParams & { detail?: 'full' | 'summary' } = {}): Promise<any> {
    try {
      // Default to full detail for comprehensive stats
      const fullParams = { detail: 'full', ...params };
      const queryString = this.buildQueryString(fullParams);
      const response = await api.get(`${this.baseUrl}firms/${firmId}/${queryString}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getFirmDashboard(firmId: number, params: StatsQueryParams = {}): Promise<FirmStatistics> {
    try {
      const queryString = this.buildQueryString(params);
      const response = await api.get(`${this.baseUrl}firms/${firmId}/dashboard/${queryString}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async compareFirms(params: ComparisonParams): Promise<FirmStatsResponse> {
    try {
      const queryString = this.buildQueryString(params);
      const response = await api.get(`${this.baseUrl}firms/comparison/${queryString}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // User Statistics
  async getUserStats(params: StatsQueryParams = {}): Promise<UserStatsResponse> {
    try {
      const queryString = this.buildQueryString(params);
      const response = await api.get(`${this.baseUrl}users/${queryString}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getUserStatsById(userId: number, params: StatsQueryParams = {}): Promise<UserStatistics> {
    try {
      const queryString = this.buildQueryString(params);
      const response = await api.get(`${this.baseUrl}users/${userId}/${queryString}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getMyStats(params: StatsQueryParams = {}): Promise<UserStatistics> {
    try {
      const queryString = this.buildQueryString(params);
      const response = await api.get(`${this.baseUrl}users/my_stats/${queryString}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Side-by-side user comparison. Backend returns {period, users[]}.
   */
  async compareUsers(
    userIds: number[],
    params: StatsQueryParams = {},
  ): Promise<{ period: any; users: Array<{ user_id: number; user_name: string; stats: any }> }> {
    const queryString = this.buildQueryString({
      ...params,
      user_ids: userIds.join(","),
    });
    const response = await api.get(`${this.baseUrl}users/compare-users/${queryString}`);
    return response.data;
  }

  async getUserDashboard(userId: number, params: StatsQueryParams = {}): Promise<UserStatistics> {
    try {
      const queryString = this.buildQueryString(params);
      const response = await api.get(`${this.baseUrl}users/${userId}/dashboard/${queryString}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getTeamOverview(params: StatsQueryParams = {}): Promise<TeamOverviewResponse> {
    try {
      const queryString = this.buildQueryString(params);
      const response = await api.get(`${this.baseUrl}users/team_overview/${queryString}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // Client Statistics
  async getClientStats(params: StatsQueryParams = {}): Promise<ClientStatsResponse> {
    try {
      const queryString = this.buildQueryString(params);
      const response = await api.get(`${this.baseUrl}clients/${queryString}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getClientStatsById(clientId: number, params: StatsQueryParams = {}): Promise<ClientStatistics> {
    try {
      const queryString = this.buildQueryString(params);
      const response = await api.get(`${this.baseUrl}clients/${clientId}/${queryString}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getTopClients(params: TopClientsParams = {}): Promise<TopClientsResponse> {
    try {
      const queryString = this.buildQueryString(params);
      const response = await api.get(`${this.baseUrl}clients/top_clients/${queryString}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // Analytics & Insights
  async getFirmOverview(params: StatsQueryParams = {}): Promise<FirmOverviewResponse> {
    try {
      const queryString = this.buildQueryString(params);
      const response = await api.get(`${this.baseUrl}analytics/firm_overview/${queryString}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getMonthlyTrends(params: {
    firm_id?: number;
    start_date?: string;
    end_date?: string;
  } = {}): Promise<{
    months: Array<{
      month: string;
      label: string;
      revenue: number;
      billable_hours: number;
      invoiced: number;
      paid: number;
    }>;
    start_date: string;
    end_date: string;
  }> {
    try {
      const queryString = this.buildQueryString(params as StatsQueryParams);
      const response = await api.get(`${this.baseUrl}analytics/monthly_trends/${queryString}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getTrendAnalysis(params: StatsQueryParams = {}): Promise<TrendAnalysisResponse> {
    try {
      const queryString = this.buildQueryString(params);
      const response = await api.get(`${this.baseUrl}analytics/trends/${queryString}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // Business Dashboard - single API call for the main dashboard
  async getBusinessDashboard(params: StatsQueryParams = {}): Promise<any> {
    try {
      const queryString = this.buildQueryString(params);
      const response = await api.get(`${this.baseUrl}analytics/business_dashboard/${queryString}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // Error handling
  private handleError(error: any): Error {
    if (error.response?.data) {
      const errorData = error.response.data as StatsErrorResponse;
      const message = errorData.error || 'An unknown error occurred';
      const details = errorData.details ? ` (${JSON.stringify(errorData.details)})` : '';
      return new Error(`${message}${details}`);
    }
    
    if (error.response?.status === 401) {
      return new Error('Unauthorized access. Please check your permissions.');
    }
    
    if (error.response?.status === 403) {
      return new Error('Insufficient permissions to access this data.');
    }
    
    if (error.response?.status === 404) {
      return new Error('Resource not found.');
    }
    
    if (error.message) {
      return new Error(error.message);
    }
    
    return new Error('An unexpected error occurred while fetching statistics.');
  }

  // Utility methods for date formatting
  formatDateForApi(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  getCurrentPeriodParams(period: 'day' | 'week' | 'month' | 'quarter' | 'year'): StatsQueryParams {
    const now = new Date();
    const params: StatsQueryParams = { period };

    switch (period) {
      case 'day':
        params.reference_date = this.formatDateForApi(now);
        break;
      case 'week':
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        params.reference_date = this.formatDateForApi(startOfWeek);
        break;
      case 'month':
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        params.reference_date = this.formatDateForApi(startOfMonth);
        break;
      case 'quarter':
        const quarter = Math.floor(now.getMonth() / 3);
        const startOfQuarter = new Date(now.getFullYear(), quarter * 3, 1);
        params.reference_date = this.formatDateForApi(startOfQuarter);
        break;
      case 'year':
        const startOfYear = new Date(now.getFullYear(), 0, 1);
        params.reference_date = this.formatDateForApi(startOfYear);
        break;
    }

    return params;
  }

  getCustomPeriodParams(startDate: Date, endDate: Date): StatsQueryParams {
    return {
      period: 'custom',
      start_date: this.formatDateForApi(startDate),
      end_date: this.formatDateForApi(endDate),
    };
  }
}

// Export singleton instance
export const statsApi = new StatsApiService();

// Export class for testing or custom instances
export default StatsApiService;
